/**
 * Coach mark (W2-14 onboarding-wizard; DESIGN.md §5.14): a ring around a part of the editor and a card next to it,
 * drawn above the app in a portal. Used by the tour (one step at a time, with Back / Next) and by "Show me"
 * (a spotlight with a short label).
 *
 *  - Non-modal: the editor stays usable (the ring does not take pointer events), so the user can do what the step
 *    asks. The card is a labelled landmark (`role="region"`), NOT a dialog: the app's keyboard router treats focus
 *    inside any `role="dialog"` as "a dialog has focus" and then drops every global binding, so the very keys a tour
 *    step asks for (play, tools, the curve picker, workspaces) would stop working while the card has focus.
 *    Escape inside the card closes it. `announce` is read out (polite live region) when it changes, so a step
 *    change is heard even while focus stays in the editor.
 *  - Focus: the card never takes focus by itself; its owner focuses it (`onCard`; it is focusable with
 *    tabIndex=-1, and is not a control, so Space and Enter on it reach the global bindings) or a control inside.
 *  - The card sits on the side the step asks for when one is given (`prefer`, UX-095 side placement), else below the
 *    target when it fits, else above, right or left, always inside the viewport and clear of `avoid` — an area the
 *    card must not cover (e.g. the tool rail the Tap and Hold buttons sit on), going to the far side of that area
 *    when the target's sides would cover it; without a target (not on screen) it waits at the bottom centre.
 *    Positions follow scrolling, resizing and layout changes.
 *  - While a modal dialog is open (the easing picker, a confirmation) the coach mark is hidden, and it comes back
 *    re-anchored when the dialog closes: it is drawn above dialogs and would cover them (UX-069).
 *  - Docked (the compact chip, round-2 UX): while a full-window surface is up (Theater, test play) or the step's
 *    target is not in the workspace the user is in, the card is replaced by a small chip at the bottom left —
 *    "Step 1 of 8" plus its controls — which never covers the primary actions of another screen (the Publish
 *    export bar, the Theater stage). The chip drops the "this part is not on screen" claim: it does not block the
 *    screen it waits for. It re-anchors to the card when the target is back.
 *  - Reduced motion: no pulse, no smooth scrolling.
 */

import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import type { MessageRef } from '../../core/util/types';
import { IconButton } from '../../ui';
import s from './Onboarding.module.css';

/** Gap between target and card, and the ring's padding around the target (px). */
const GAP = 12;
const RING_PAD = 6;
const MARGIN = 8;

export interface Rect {
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

/** Where the card sits: beside the target, 'away' from it (beside the area to keep clear), or 'center'. */
export type Side = 'bottom' | 'top' | 'right' | 'left' | 'away' | 'center';

export interface CardPlacement {
  readonly left: number;
  readonly top: number;
  readonly side: Side;
}

function overlaps(a: Rect, b: Rect): boolean {
  return a.left < b.left + b.width && b.left < a.left + a.width && a.top < b.top + b.height && b.top < a.top + a.height;
}

/**
 * Where the card goes (pure; see the module header). `avoid` is an area the card must not cover (e.g. the tool rail
 * for the Tap and Hold steps, whose button lives on it: the card then sits on the rail's right edge, beside the
 * ringed button and over the top of the lane, which leaves the rest of the lane free to click in). `prefer` names
 * the side of the target to try first (UX-095 side placement): it is tried before the usual below / above / right /
 * left order, and like them it only wins when it fits clear of `avoid`. The order is: `prefer` (when given), below,
 * above, right, left — each clear of `avoid`; beside `avoid` on its far sides (right, left: bottom-aligned with it;
 * below, above), covering neither; else the first place beside the target.
 */
export function placeCard(
  target: Rect | null,
  card: { readonly width: number; readonly height: number },
  viewport: { readonly width: number; readonly height: number },
  avoid: Rect | null = null,
  prefer?: Side,
): CardPlacement {
  const clampX = (x: number): number => Math.max(MARGIN, Math.min(viewport.width - card.width - MARGIN, x));
  const clampY = (y: number): number => Math.max(MARGIN, Math.min(viewport.height - card.height - MARGIN, y));
  const fitsX = (x: number): boolean => x >= MARGIN && x + card.width <= viewport.width - MARGIN;
  const fitsY = (y: number): boolean => y >= MARGIN && y + card.height <= viewport.height - MARGIN;
  const box = (p: CardPlacement): Rect => ({ left: p.left, top: p.top, width: card.width, height: card.height });
  if (target === null) {
    return {
      left: clampX((viewport.width - card.width) / 2),
      top: clampY(viewport.height - card.height - 48),
      side: 'center',
    };
  }
  const cx = target.left + target.width / 2;
  const cy = target.top + target.height / 2;

  const beside: CardPlacement[] = [];
  const bottom = target.top + target.height + GAP;
  if (fitsY(bottom)) beside.push({ left: clampX(cx - card.width / 2), top: bottom, side: 'bottom' });
  const top = target.top - GAP - card.height;
  if (fitsY(top)) beside.push({ left: clampX(cx - card.width / 2), top, side: 'top' });
  const right = target.left + target.width + GAP;
  if (fitsX(right)) beside.push({ left: right, top: clampY(cy - card.height / 2), side: 'right' });
  const left = target.left - GAP - card.width;
  if (fitsX(left)) beside.push({ left, top: clampY(cy - card.height / 2), side: 'left' });
  // `prefer` (UX-095 side placement): the named side is tried first, like any other beside placement — it must still
  // fit and stay clear of `avoid`. 'away' and 'center' are not sides of the target, so they never come first here.
  if (prefer !== undefined && prefer !== 'away' && prefer !== 'center') {
    const wanted = beside.findIndex((p) => p.side === prefer);
    if (wanted > 0) beside.unshift(...beside.splice(wanted, 1));
  }

  const clear = (p: CardPlacement): boolean => avoid === null || !overlaps(box(p), avoid);
  const best = beside.find(clear);
  if (best !== undefined) return best;

  if (avoid !== null) {
    const away: CardPlacement[] = [];
    // Bottom-aligned with the area: over the lower part of whatever is beside it (in the Chart workspace the
    // preview below its judge line, where notes are hidden anyway), keeping its upper part visible.
    const alignedTop = clampY(avoid.top + avoid.height - card.height);
    const alignedLeft = clampX(target.left);
    const aRight = avoid.left + avoid.width + GAP;
    if (fitsX(aRight)) away.push({ left: aRight, top: alignedTop, side: 'away' });
    const aLeft = avoid.left - GAP - card.width;
    if (fitsX(aLeft)) away.push({ left: aLeft, top: alignedTop, side: 'away' });
    const aBelow = avoid.top + avoid.height + GAP;
    if (fitsY(aBelow)) away.push({ left: alignedLeft, top: aBelow, side: 'away' });
    const aAbove = avoid.top - GAP - card.height;
    if (fitsY(aAbove)) away.push({ left: alignedLeft, top: aAbove, side: 'away' });
    const free = away.find((p) => clear(p) && !overlaps(box(p), target));
    if (free !== undefined) return free;
  }

  const first = beside[0];
  if (first !== undefined) return first;
  // The target fills the screen (e.g. the timeline at a small size): the card goes inside, at the bottom.
  return { left: clampX(cx - card.width / 2), top: clampY(viewport.height - card.height - 48), side: 'center' };
}

export interface CoachMarkProps {
  readonly target: Element | null;
  /** An area the card must not cover (e.g. the tool rail the Tap and Hold buttons sit on). */
  readonly avoid?: Element | null;
  /** The side of the target to place the card on first (UX-095 side placement); still kept clear of `avoid`. */
  readonly prefer?: Side | undefined;
  readonly title: string;
  readonly children?: ReactNode;
  readonly footer?: ReactNode;
  /** Small label above the title ("Step 2 of 8"). */
  readonly label?: string;
  readonly closeLabel: MessageRef;
  readonly onClose: () => void;
  readonly reducedMotion: boolean;
  readonly kind?: 'tour' | 'spotlight';
  /** Receives the card element when it is attached (null when it goes), for the owner's focus handling. */
  readonly onCard?: (el: HTMLDivElement | null) => void;
  /** Text read out by screen readers whenever it changes (e.g. "Step 2 of 8: Play the song"). */
  readonly announce?: string;
  /**
   * Compact chip instead of the card (round-2 UX): Theater, test play or another full-window surface is up, or the
   * target is not in the current workspace. The chip is docked at the bottom left and only carries `chip`.
   */
  readonly docked?: boolean | undefined;
  /** Controls of the docked chip (the card's footer does not fit there). */
  readonly chip?: ReactNode;
}

/**
 * A dialog of the app covers the editor: a modal (Export, a confirmation), a popover such as the easing picker, or
 * the test-play pause card. The coach mark stands back while one is open: its layer is above dialogs and would
 * cover them (UX-069); it comes back, re-anchored, when the dialog closes. Side sheets (Help, whose "Show me"
 * spotlights sit beside it) do not count.
 */
export function modalDialogOpen(root: ParentNode = document): boolean {
  for (const el of root.querySelectorAll('[role="dialog"], [role="alertdialog"], dialog[open]')) {
    if (el.hasAttribute('data-side')) continue;
    return true;
  }
  return false;
}

/** The on-screen box of an element, or null when it is detached or not laid out. */
function rectOf(el: Element | null | undefined): Rect | null {
  if (el === null || el === undefined || !el.isConnected) return null;
  const r = el.getBoundingClientRect();
  return r.width > 0 || r.height > 0 ? { left: r.left, top: r.top, width: r.width, height: r.height } : null;
}

/**
 * Is the element part of the layout now? `checkVisibility` when the browser has it (a found-but-hidden target
 * docks the card); without it (jsdom in unit tests) a connected element counts as shown.
 */
function isShown(el: Element): boolean {
  if (!el.isConnected) return false;
  const self = el as Element & { checkVisibility?: (o?: object) => boolean };
  return typeof self.checkVisibility !== 'function' || self.checkVisibility({ checkVisibilityCSS: true });
}

export function CoachMark(props: CoachMarkProps) {
  const { target, avoid, prefer, reducedMotion, onClose } = props;
  const layer = useRef<HTMLDivElement>(null);
  const ring = useRef<HTMLDivElement>(null);
  /** The card element only (placement target). The docked chip has its own ref and is never positioned here. */
  const card = useRef<HTMLDivElement>(null);
  const { onCard } = props;
  // The target was named but has no box (hidden, or it left the workspace between lookups): the card docks too
  // rather than parking over the primary actions of whatever is on screen (round-2 UX).
  const [missing, setMissing] = useState(false);
  const docked = props.docked === true || missing;
  // The interval in the placement effect outlives a render by a tick: it must see the current `docked`, or it
  // writes the card's inline left/top onto the chip just mounted in its place (the chip's CSS `bottom` then
  // stretches it into a tall capsule over the screen — round-2 leftover). Layout effect: the sync happens before
  // any timer callback, so a stale tick cannot slip between the commit and the ref update.
  const dockedRef = useRef(docked);
  useLayoutEffect(() => {
    dockedRef.current = docked;
  }, [docked]);
  const setCard = useCallback(
    (el: HTMLDivElement | null) => {
      // Placement reads this ref: null while the chip is up, so a stale tick cannot move the chip.
      card.current = el !== null && el.dataset.testid === 'coach-chip' ? null : el;
      onCard?.(el);
    },
    [onCard],
  );
  const setChip = useCallback(
    (el: HTMLDivElement | null) => {
      card.current = null;
      // React keeps the same <div> for card and chip (same type, same slot): the card's placement styles written
      // by the effect below are still on it. The chip docks by CSS (bottom left), so drop them — with `top` kept
      // next to the chip's `bottom`, the box stretched to a tall capsule over the export bar (round-2 leftover).
      if (el !== null) {
        el.style.left = '';
        el.style.top = '';
        el.style.width = '';
        el.style.height = '';
        delete el.dataset.side;
      }
      onCard?.(el);
    },
    [onCard],
  );
  const titleId = useId();
  const bodyId = useId();

  // Positions are written straight to the elements (no React state per frame).
  useEffect(() => {
    const place = (): void => {
      const l = layer.current;
      const covered = modalDialogOpen();
      if (l !== null && l.hidden !== covered) l.hidden = covered;
      if (covered) return;
      const gone = target !== null && !isShown(target);
      // A named target that is not on screen: dock (see `missing`). This runs even while docked, so the card
      // comes back when the target reappears.
      setMissing(gone);
      if (dockedRef.current || gone) return;
      const rect = rectOf(target);
      const c = card.current;
      if (c === null) return;
      const g = ring.current;
      if (g !== null) {
        if (rect === null) g.hidden = true;
        else {
          g.hidden = false;
          g.style.left = `${String(rect.left - RING_PAD)}px`;
          g.style.top = `${String(rect.top - RING_PAD)}px`;
          g.style.width = `${String(rect.width + RING_PAD * 2)}px`;
          g.style.height = `${String(rect.height + RING_PAD * 2)}px`;
        }
      }
      const pos = placeCard(
        rect,
        { width: c.offsetWidth, height: c.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
        rectOf(avoid),
        prefer,
      );
      c.style.left = `${String(pos.left)}px`;
      c.style.top = `${String(pos.top)}px`;
      c.dataset.side = pos.side;
    };
    place();
    const timer = window.setInterval(place, 250);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    // Dialogs open in portals at the end of <body>: notice them at once, not at the next tick.
    const watch = typeof MutationObserver === 'function' ? new MutationObserver(place) : null;
    watch?.observe(document.body, { childList: true });
    return () => {
      watch?.disconnect();
      window.clearInterval(timer);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [target, avoid, prefer, docked]);

  // Bring the target into view once per target (not while the card is docked away from it).
  useEffect(() => {
    if (target === null || docked) return;
    const el = target as Element & { scrollIntoView?: (o?: ScrollIntoViewOptions) => void };
    if (typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: reducedMotion ? 'auto' : 'smooth' });
    }
  }, [target, reducedMotion, docked]);

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    if (e.key !== 'Escape') return;
    e.preventDefault();
    e.stopPropagation();
    onClose();
  };

  return createPortal(
    <div
      ref={layer}
      className={s.coachLayer}
      data-reduced-motion={reducedMotion ? 'true' : undefined}
      data-kind={props.kind ?? 'tour'}
      data-docked={docked ? 'true' : undefined}
    >
      <div ref={ring} className={s.coachRing} aria-hidden="true" hidden={docked} />
      {docked ? (
        <div
          key="coach-chip"
          ref={setChip}
          className={s.coachChip}
          role="region"
          aria-label={props.label ?? props.title}
          tabIndex={-1}
          onKeyDown={onKeyDown}
          data-testid="coach-chip"
        >
          <span className={s.coachChipLabel}>{props.label ?? props.title}</span>
          {props.chip}
          {props.announce !== undefined && (
            <p className={s.srOnly} aria-live="polite" aria-atomic="true">
              {props.announce}
            </p>
          )}
        </div>
      ) : (
        <div
          key="coach-card"
          ref={setCard}
          className={s.coachCard}
          role="region"
          aria-labelledby={titleId}
          aria-describedby={props.children === undefined ? undefined : bodyId}
          tabIndex={-1}
          onKeyDown={onKeyDown}
        >
          <div className={s.coachHeader}>
            {props.label !== undefined && <span className={s.coachLabel}>{props.label}</span>}
            <IconButton icon="X" size="sm" label={props.closeLabel} onClick={onClose} />
          </div>
          <h2 id={titleId} className={s.coachTitle}>
            {props.title}
          </h2>
          {props.children !== undefined && (
            <div id={bodyId} className={s.coachBody}>
              {props.children}
            </div>
          )}
          {props.footer !== undefined && <div className={s.coachFooter}>{props.footer}</div>}
          {props.announce !== undefined && (
            <p className={s.srOnly} aria-live="polite" aria-atomic="true">
              {props.announce}
            </p>
          )}
        </div>
      )}
    </div>,
    document.body,
  );
}
